function initSwiper(target, config = {}) {
    const elements = typeof target === "string"
        ? document.querySelectorAll(target)
        : [target];

    elements.forEach((el) => {
        if (!el || el.swiper) return;

        new Swiper(el, {
            slidesPerView: config.slidesPerView || 3,
            spaceBetween: config.spaceBetween || 12,
            pagination: {
                el: config.paginationEl || el.querySelector(".swiper-pagination"),
                clickable: config.clickable || false,
            },
            breakpoints: config.breakpoints || {},
        });
    });
}

/**
 * Animated count-up for themed counter elements (decimals supported; comma decimals OK).
 * Starts once the element crosses into the viewport (no animation while off-screen).
 * @param {string|Element} target CSS selector or a single Element
 */
function initCounter(target) {
    const nodes =
        typeof target === 'string' ? document.querySelectorAll(target) : [target];

    nodes.forEach((counter) => {
        if (!counter || counter.dataset.nwCounterObserved) return;

        const finalDisplay = counter.textContent.trim();
        const commaAsDot = finalDisplay.replace(/,/g, '.');
        const targetValue = parseFloat(commaAsDot);

        if (Number.isNaN(targetValue) || targetValue < 0) return;

        let decimalPlaces = 0;
        const dotIdx = commaAsDot.indexOf('.');
        if (dotIdx !== -1) {
            decimalPlaces = Math.min(8, commaAsDot.length - dotIdx - 1);
        }

        const formatCount = (value) =>
            decimalPlaces > 0 ? value.toFixed(decimalPlaces) : String(Math.ceil(value));

        counter.dataset.nwCounterObserved = 'true';

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            counter.textContent = finalDisplay;
            return;
        }

        counter.textContent = formatCount(0);

        const startAnimation = () => {
            if (counter.dataset.nwCounterDone === 'true') return;
            counter.dataset.nwCounterDone = 'true';

            let count = 0;

            const updateCounter = () => {
                const increment = targetValue / 100;
                const eps =
                    decimalPlaces > 0 ? 0.5 * Math.pow(10, -decimalPlaces) : 1e-9;

                if (count < targetValue - eps) {
                    count = Math.min(count + increment, targetValue);
                    counter.textContent = formatCount(count);
                    requestAnimationFrame(updateCounter);
                } else {
                    counter.textContent = finalDisplay;
                }
            };

            updateCounter();
        };

        const runWhenVisible = () => {
            if ('IntersectionObserver' in window) {
                const io = new IntersectionObserver(
                    (entries, obs) => {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) return;
                            obs.unobserve(entry.target);
                            startAnimation();
                        });
                    },
                    { rootMargin: '0px 0px', threshold: 0.15 }
                );
                io.observe(counter);
            } else {
                startAnimation();
            }
        };

        requestAnimationFrame(runWhenVisible);
    });
}

function nwRunSectionInitializers() {
    if (typeof initVideoControls === 'function') initVideoControls();
    if (typeof initFAQ === 'function') initFAQ();
    initCounter('.number');
    initCounter('.nw-count-text');
    if (typeof initVideoSwiper === 'function') initVideoSwiper();
    if (typeof initReviewSwiper === 'function') initReviewSwiper();
}

document.addEventListener('DOMContentLoaded', nwRunSectionInitializers);
document.addEventListener('shopify:section:load', nwRunSectionInitializers);


document.addEventListener('DOMContentLoaded', () => {
    /////////////// Scrollbar Width Measurement //////////////
    function calculateScrollbarWidth() {
        // Use the measurement div from theme.liquid or create one dynamically
        let measureDiv = document.getElementById('scrollbar-measure');
        
        // Create measurement div if it doesn't exist
        if (!measureDiv) {
            measureDiv = document.createElement('div');
            measureDiv.id = 'scrollbar-measure';
            measureDiv.style.cssText = 'position: absolute; visibility: hidden; overflow: scroll; width: 100px; height: 100px; top: -9999px; left: -9999px;';
            document.body.appendChild(measureDiv);
        }
        
        // Ensure the div has content larger than its container to force scrollbar
        let innerDiv = measureDiv.querySelector('.scrollbar-measure-inner');
        if (!innerDiv) {
            innerDiv = document.createElement('div');
            innerDiv.className = 'scrollbar-measure-inner';
            innerDiv.style.cssText = 'width: 200px; height: 200px;';
            measureDiv.appendChild(innerDiv);
        }
        
        // Force reflow to ensure measurements are accurate
        void measureDiv.offsetWidth;
        
        // Calculate scrollbar width using the measurement div
        let scrollbarWidth = measureDiv.offsetWidth - measureDiv.clientWidth;
        
        // Fallback method if measurement div returns 0
        if (scrollbarWidth === 0 || isNaN(scrollbarWidth)) {
            // Method 1: Compare window dimensions
            const windowWidth = window.innerWidth;
            const documentClientWidth = document.documentElement.clientWidth;
            
            if (windowWidth !== documentClientWidth) {
                scrollbarWidth = windowWidth - documentClientWidth;
            } else {
                // Method 2: Temporarily force scrollbar on body to measure
                const originalOverflow = document.body.style.overflow;
                const originalOverflowY = document.body.style.overflowY;
                const originalHeight = document.body.style.height;
                
                document.body.style.overflow = 'scroll';
                document.body.style.overflowY = 'scroll';
                document.body.style.height = '100%';
                
                // Force reflow
                void document.body.offsetHeight;
                
                const bodyScrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
                
                // Restore original styles
                document.body.style.overflow = originalOverflow;
                document.body.style.overflowY = originalOverflowY;
                document.body.style.height = originalHeight;
                
                scrollbarWidth = bodyScrollbarWidth;
            }
        }
        
        // Ensure we have a valid value (at least 0)
        scrollbarWidth = Math.max(0, scrollbarWidth || 0);
        
        // Set CSS variable on HTML element
        document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
        
        return scrollbarWidth;
    }

    setTimeout(function() {
        calculateScrollbarWidth();
    }, 0);

    let resizeTimeout;
    function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function() {
            calculateScrollbarWidth();
        }, 150);
    }

    window.addEventListener('resize', handleResize, { passive: true });

    window.addEventListener('orientationchange', function() {
        setTimeout(function() {
            calculateScrollbarWidth();
        }, 100);
    }, { passive: true });

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize, { passive: true });
    }


    /////////////// According Smooth Toggle Animation //////////////
    class Accordion {
        constructor(el) {
            // Store the <details> element
            this.el = el;
            // Store the <summary> element
            this.summary = el.querySelector('summary');
            // Store the <div class="content"> element
            this.content = el.querySelector('.accordion__content');
        
            // Store the animation object (so we can cancel it if needed)
            this.animation = null;
            // Store if the element is closing
            this.isClosing = false;
            // Store if the element is expanding
            this.isExpanding = false;
            // Detect user clicks on the summary element
            this.summary.addEventListener('click', (e) => this.onClick(e));
        }
        
        onClick(e) {
            // Stop default behaviour from the browser
            e.preventDefault();
            // Add an overflow on the <details> to avoid content overflowing
            this.el.style.overflow = 'hidden';
            // Check if the element is being closed or is already closed
            if (this.isClosing || !this.el.open) {
            this.open();
            // Check if the element is being openned or is already open
            } else if (this.isExpanding || this.el.open) {
            this.shrink();
            }
        }
        
        shrink() {
            // Set the element as "being closed"
            this.isClosing = true;
            
            // Store the current height of the element
            const startHeight = `${this.el.offsetHeight}px`;
            // Calculate the height of the summary
            const endHeight = `${this.summary.offsetHeight}px`;
            
            // If there is already an animation running
            if (this.animation) {
            // Cancel the current animation
            this.animation.cancel();
            }
            
            // Start a WAAPI animation
            this.animation = this.el.animate({
            // Set the keyframes from the startHeight to endHeight
            height: [startHeight, endHeight]
            }, {
            duration: 400,
            easing: 'ease-out'
            });
            
            // When the animation is complete, call onAnimationFinish()
            this.animation.onfinish = () => this.onAnimationFinish(false);
            // If the animation is cancelled, isClosing variable is set to false
            this.animation.oncancel = () => this.isClosing = false;
        }
        
        open() {
            // Apply a fixed height on the element
            this.el.style.height = `${this.el.offsetHeight}px`;
            // Force the [open] attribute on the details element
            this.el.open = true;
            // Wait for the next frame to call the expand function
            window.requestAnimationFrame(() => this.expand());
        }
        
        expand() {
            // Set the element as "being expanding"
            this.isExpanding = true;
            // Get the current fixed height of the element
            const startHeight = `${this.el.offsetHeight}px`;
            // Calculate the open height of the element (summary height + content height)
            const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
            
            // If there is already an animation running
            if (this.animation) {
            // Cancel the current animation
            this.animation.cancel();
            }
            
            // Start a WAAPI animation
            this.animation = this.el.animate({
            // Set the keyframes from the startHeight to endHeight
            height: [startHeight, endHeight]
            }, {
            duration: 400,
            easing: 'ease-out'
            });
            // When the animation is complete, call onAnimationFinish()
            this.animation.onfinish = () => this.onAnimationFinish(true);
            // If the animation is cancelled, isExpanding variable is set to false
            this.animation.oncancel = () => this.isExpanding = false;
        }
        
        onAnimationFinish(open) {
            // Set the open attribute based on the parameter
            this.el.open = open;
            // Clear the stored animation
            this.animation = null;
            // Reset isClosing & isExpanding
            this.isClosing = false;
            this.isExpanding = false;
            // Remove the overflow hidden and the fixed height
            this.el.style.height = this.el.style.overflow = '';
        }
    }

    document.querySelectorAll('.accordion.smooth details').forEach((el) => {
        new Accordion(el);
    });


    /////////////// Close Exsting According //////////////
    class AccordionToggle {
        constructor(el, accordionGroup) {
          this.el = el;
          this.summary = el.querySelector('summary');
          this.content = el.querySelector('.accordion__content');
          
          this.animation = null;
          this.isClosing = false;
          this.isExpanding = false;
          this.accordionGroup = accordionGroup;
          
          // Set aria-expanded based on the initial open state
          this.updateAriaExpanded(this.el.open);
          
          this.summary.addEventListener('click', (e) => this.onClick(e));
        }
      
        onClick(e) {
          e.preventDefault();
          this.el.style.overflow = 'hidden';
    
          // Close all other accordions
          this.accordionGroup.closeAllExcept(this);
    
          if (this.isClosing || !this.el.open) {
            this.open();
          } else if (this.isExpanding || this.el.open) {
            this.shrink();
          }
        }
      
        shrink() {
          this.isClosing = true;
          const startHeight = `${this.el.offsetHeight}px`;
          const endHeight = `${this.summary.offsetHeight}px`;
    
          if (this.animation) {
            this.animation.cancel();
          }
    
          this.animation = this.el.animate({
            height: [startHeight, endHeight]
          }, {
            duration: 400,
            easing: 'ease-out'
          });
    
          this.animation.onfinish = () => this.onAnimationFinish(false);
          this.animation.oncancel = () => this.isClosing = false;
        }
      
        open() {
          this.el.style.height = `${this.el.offsetHeight}px`;
          this.el.open = true;
          
          this.accordionGroup.currentAccordion = this;
      
          window.requestAnimationFrame(() => this.expand());
        }
      
        expand() {
          this.isExpanding = true;
          const startHeight = `${this.el.offsetHeight}px`;
          const endHeight = `${this.summary.offsetHeight + this.content.scrollHeight}px`; // Use scrollHeight for precise measurement
    
          if (this.animation) {
            this.animation.cancel();
          }
    
          this.animation = this.el.animate({
            height: [startHeight, endHeight]
          }, {
            duration: 400,
            easing: 'ease-out'
          });
    
          this.animation.onfinish = () => this.onAnimationFinish(true);
          this.animation.oncancel = () => this.isExpanding = false;
        }
      
        onAnimationFinish(open) {
          this.el.open = open;
          this.animation = null;
          this.isClosing = false;
          this.isExpanding = false;
          this.el.style.height = '';
          this.el.style.overflow = '';
    
          this.updateAriaExpanded(open);
          
          if (!open) {
            this.accordionGroup.currentAccordion = null;
          }
        }
      
        updateAriaExpanded(isExpanded) {
          this.summary.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        }
      
        close() {
          if (this.el.open) {
            this.shrink();
          }
        }
      }
      
      class AccordionGroup {
        constructor() {
          this.currentAccordion = null;
          this.accordions = [];
        }
      
        init() {
          document.querySelectorAll('.accordion.smooth:not(.smooth-v2) details').forEach(el => {
            //el.open = false;
            const accordion = new AccordionToggle(el, this);
            this.accordions.push(accordion);
          });
        }
      
        closeAllExcept(currentAccordion) {
          this.accordions.forEach(accordion => {
            if (accordion !== currentAccordion) {
              accordion.close();
            }
          });
        }
    }

    const accordionGroup = new AccordionGroup();
    accordionGroup.init();

    /** Pixels to subtract when scrolling past sticky header (theme sets --header-height; +16px gap). */
    function nwStickyHeaderScrollOffsetPx() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
      let h = parseFloat(raw);
      if (!Number.isFinite(h) || h <= 0) {
        const hdr =
          document.querySelector('sticky-header') ||
          document.querySelector('.shopify-section-header') ||
          document.querySelector('.section-header');
        h = hdr ? hdr.offsetHeight : 0;
      }
      return (Number.isFinite(h) ? h : 0) + 16;
    }

    // Smooth scroll to anchor — must run after DOM exists (this script loads in <head>)
    document.addEventListener('click', function (e) {
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target.closest('a[href*="#"]');
      if (!a) return;
      const hrefAttr = a.getAttribute('href');
      if (!hrefAttr || hrefAttr === '#') return;
      if (a.target === '_blank') return;

      let hash = '';
      try {
        hash = new URL(a.href).hash;
      } catch (err) {
        return;
      }
      if (!hash || hash === '#') return;

      const id = decodeURIComponent(hash.slice(1));
      if (!id) return;

      const targetEl = document.getElementById(id);
      if (!targetEl) return;

      try {
        const linkUrl = new URL(a.href);
        if (linkUrl.origin !== window.location.origin) return;
        if (linkUrl.pathname !== window.location.pathname || linkUrl.search !== window.location.search) {
          return;
        }
      } catch (err2) {
        return;
      }

      e.preventDefault();
      for (let n = targetEl; n && n !== document.documentElement; n = n.parentElement) {
        if (n.classList && n.classList.contains('scroll-trigger')) {
          n.classList.remove('scroll-trigger--offscreen');
          n.classList.add('scroll-trigger--design-mode');
        }
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const off = nwStickyHeaderScrollOffsetPx();
          const top = targetEl.getBoundingClientRect().top + window.scrollY - off;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          try {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          } catch (err3) {}
        });
      });
    });


    /////////////// Menu Drawer Accordion (header-drawer) - smooth slideToggle via WAAPI //////////////
    (function() {
      const DURATION = 300;
      const EASING = 'ease-out';
      const ACCORDION_MAX_WIDTH = 1023;
      const isMobileView = () => window.innerWidth <= ACCORDION_MAX_WIDTH;

      function animateSubmenu(submenu, fromHeight, toHeight, onFinish) {
        const anim = submenu.animate(
          { height: [fromHeight + 'px', toHeight + 'px'] },
          { duration: DURATION, easing: EASING }
        );
        anim.onfinish = () => {
          submenu.style.height = toHeight === 0 ? '0' : '';
          onFinish && onFinish();
        };
      }

      window.menuDrawerAccordionClose = function(detailsElement, done) {
        if (window.innerWidth > ACCORDION_MAX_WIDTH) { done(); return; }
        const submenu = detailsElement.querySelector(':scope > .menu-drawer__submenu');
        if (!submenu) { done(); return; }
        const startHeight = submenu.offsetHeight;
        if (startHeight === 0) { done(); return; }
        submenu.style.height = startHeight + 'px';
        detailsElement.open = true;
        animateSubmenu(submenu, startHeight, 0, done);
      };

      function initMenuDrawerAccordion() {
        const drawer = document.querySelector('header-drawer');
        if (!drawer) return;
        const mainDetails = drawer.querySelector('#Details-menu-drawer-container');
        if (!mainDetails) return;

        drawer.querySelectorAll('.menu-drawer__navigation details').forEach((detailsEl) => {
          if (detailsEl === mainDetails) return;
          const submenu = detailsEl.querySelector(':scope > .menu-drawer__submenu');
          if (!submenu) return;

          detailsEl.addEventListener('toggle', function() {
            if (!isMobileView()) return;
            if (this._accordionClosing) return;
            if (this.open) {
              const endHeight = submenu.scrollHeight;
              submenu.style.height = '0';
              requestAnimationFrame(() => {
                animateSubmenu(submenu, 0, endHeight, () => { submenu.style.height = ''; });
              });
            } else {
              const startHeight = submenu.offsetHeight;
              if (startHeight === 0) return;
              this.open = true;
              submenu.style.height = startHeight + 'px';
              this._accordionClosing = true;
              animateSubmenu(submenu, startHeight, 0, () => {
                this.removeAttribute('open');
                submenu.style.height = '0';
                this._accordionClosing = false;
              });
            }
          });
        });

        drawer.querySelectorAll('[data-megamenu-trigger]').forEach((trigger) => {
          const submenu = trigger.querySelector('.menu-drawer__submenu');
          if (!submenu) return;
          const observer = new MutationObserver((mutations) => {
            if (!isMobileView()) {
              submenu.style.height = trigger.classList.contains('is-open') ? 'auto' : '0';
              return;
            }
            mutations.forEach((m) => {
              if (m.attributeName === 'class') {
                if (trigger.classList.contains('is-open')) {
                  submenu.style.height = 'auto';
                  const endHeight = submenu.offsetHeight;
                  submenu.style.height = '0';
                  requestAnimationFrame(() => {
                    animateSubmenu(submenu, 0, endHeight, () => {
                      submenu.style.height = 'auto';
                    });
                  });
                } else {
                  const startHeight = submenu.offsetHeight;
                  if (startHeight > 0) {
                    submenu.style.height = startHeight + 'px';
                    animateSubmenu(submenu, startHeight, 0, () => { submenu.style.height = '0'; });
                  }
                }
              }
            });
          });
          observer.observe(trigger, { attributes: true });
        });
      }
      initMenuDrawerAccordion();
    })();

    function nwMegamenuUnlockAndScrollToExplore() {
      const el = document.getElementById('explore-collection');
      if (!el) return;
      for (let n = el; n && n !== document.documentElement; n = n.parentElement) {
        if (n.classList && n.classList.contains('scroll-trigger')) {
          n.classList.remove('scroll-trigger--offscreen');
          n.classList.add('scroll-trigger--design-mode');
        }
      }
      el.querySelectorAll('.scroll-trigger').forEach((node) => {
        node.classList.remove('scroll-trigger--offscreen');
        node.classList.add('scroll-trigger--design-mode');
      });
      const off = nwStickyHeaderScrollOffsetPx();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const y = el.getBoundingClientRect().top + window.scrollY - off;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
          try {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          } catch (err) {}
        });
      });
    }
    window.nwMegamenuUnlockAndScrollToExplore = nwMegamenuUnlockAndScrollToExplore;

    document.addEventListener(
      'click',
      function (e) {
        const a = e.target.closest('a[href*="#explore-collection"][data-nw-megamenu-tab]');
        if (!a) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const tabIdx = parseInt(a.getAttribute('data-nw-megamenu-tab'), 10);
        if (Number.isNaN(tabIdx)) return;

        let url;
        try {
          url = new URL(a.href, window.location.href);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin) return;
        if (!url.hash || url.hash.indexOf('explore-collection') === -1) return;

        function pathNorm(p) {
          if (!p || p === '/') return '/';
          return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
        }
        const samePage = pathNorm(url.pathname) === pathNorm(window.location.pathname);

        if (samePage) {
          e.preventDefault();
          e.stopPropagation();
          sessionStorage.removeItem('nwMegamenuTab');
          document.dispatchEvent(new CustomEvent('nw:product-tab:activate', { detail: { index: tabIdx } }));
          setTimeout(() => nwMegamenuUnlockAndScrollToExplore(), 0);
        } else {
          sessionStorage.setItem('nwMegamenuTab', String(tabIdx));
        }
      },
      true
    );

    /////////////// Header mega-menu: open on hover (desktop) //////////////
    (function initNwMegamenuHover() {
      const mq = window.matchMedia('(min-width: 990px)');
      const CLOSE_DELAY_MS = 180;
      let closeTimer = null;

      function isDesktop() {
        return mq.matches;
      }

      function cancelScheduledClose() {
        if (closeTimer !== null) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      }

      function closeHost(host) {
        if (!host) return;
        if (typeof host.close === 'function') {
          host.close();
        } else {
          const details = host.querySelector('details.mega-menu');
          if (details) {
            details.removeAttribute('open');
            const summary = details.querySelector('summary');
            if (summary) summary.setAttribute('aria-expanded', 'false');
          }
        }
      }

      function closeOtherMegamenus(keepHost) {
        document.querySelectorAll('header-menu details.mega-menu[open]').forEach((details) => {
          const host = details.closest('header-menu');
          if (host && host !== keepHost) closeHost(host);
        });
      }

      function openMegamenu(host) {
        const details = host.querySelector('details.mega-menu');
        if (!details) return;
        closeOtherMegamenus(host);
        if (!details.open) {
          details.open = true;
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'true');
        }
      }

      function scheduleClose(host) {
        cancelScheduledClose();
        closeTimer = setTimeout(() => {
          closeTimer = null;
          if (!isDesktop()) return;
          closeHost(host);
        }, CLOSE_DELAY_MS);
      }

      function bindMegamenuHost(host) {
        if (host.dataset.nwMegamenuHoverBound) return;
        host.dataset.nwMegamenuHoverBound = '1';

        host.addEventListener('mouseenter', () => {
          if (!isDesktop()) return;
          cancelScheduledClose();
          openMegamenu(host);
        });

        host.addEventListener('mouseleave', () => {
          if (!isDesktop()) return;
          scheduleClose(host);
        });

        const summary = host.querySelector('details.mega-menu summary');
        if (summary) {
          summary.addEventListener('click', (e) => {
            if (!isDesktop()) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

            const link = summary.querySelector('a.mega-menu__summary-link[href]');
            if (!link || !link.href) return;

            e.preventDefault();

            const details = summary.closest('details');
            summary.setAttribute('aria-expanded', details?.open ? 'true' : 'false');
            window.location.assign(link.href);
          });
        }
      }

      function scanMegamenuHosts() {
        document.querySelectorAll('header-menu').forEach((host) => {
          if (host.querySelector('details.mega-menu')) bindMegamenuHost(host);
        });
      }

      scanMegamenuHosts();

      mq.addEventListener('change', () => {
        if (!isDesktop()) {
          cancelScheduledClose();
          document.querySelectorAll('header-menu details.mega-menu[open]').forEach((details) => {
            closeHost(details.closest('header-menu'));
          });
        }
      });

      document.addEventListener('shopify:section:load', () => {
        scanMegamenuHosts();
      });
    })();

    
});
